import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, ShieldOff, ArrowLeft, X } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import CasesQueue from '@/components/admin/cases/CasesQueue';

// The dedicated support queue. Cases are the front door for customer support,
// so this is where daily triage happens: the status filter chosen above the
// queue narrows the working list, which is searched and worked from here.
export default function AdminCases({ embedded = false, status = 'new' }) {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const { user, isLoadingAuth } = useAuth();

  const customerFilter = params.get('customer') || '';

  const { data: allCases = [], isLoading } = useQuery({
    queryKey: ['crm-cases'],
    queryFn: () => base44.entities.Case.list('-last_activity_at', 500),
    enabled: user?.role === 'admin',
  });

  // Live queue — a new or updated case lands without a refresh.
  useEffect(() => {
    if (user?.role !== 'admin') return;
    const unsubscribe = base44.entities.Case.subscribe(() =>
      qc.invalidateQueries({ queryKey: ['crm-cases'] })
    );
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [user, qc]);

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  const cases = customerFilter
    ? allCases.filter((c) => String(c.customer_email || '').toLowerCase() === customerFilter.toLowerCase())
    : allCases;

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#0d0d0f] pt-16'}>
      {/* The CRM page supplies the page header when this queue is a tab. */}
      {!embedded && (
      <section className="py-10 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">SUPPORT</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">CASES</h1>
          <p className="text-white/30 text-sm mt-1">
            Every conversation, on every channel, in one queue.
          </p>
        </div>
      </section>
      )}

      <section className="px-6 lg:px-10 pt-8 pb-10 max-w-6xl mx-auto space-y-6">
        {customerFilter && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 border border-primary/40 bg-primary/10 text-primary text-[10px] font-bold tracking-widest px-3 py-1.5">
              CUSTOMER: {customerFilter}
              <button
                onClick={() => setParams({})}
                className="text-primary/70 hover:text-primary transition-colors"
                aria-label="Clear customer filter"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          </div>
        )}

        <CasesQueue
          key={customerFilter}
          cases={cases}
          isLoading={isLoading}
          status={status}
        />
      </section>
    </div>
  );
}